/**
 * BonusStructureDisplayForPartnersPage — GREEN-only, light theme.
 * Sections: bonus-tier table (tier, min-volume, bonus, active window),
 * partner-specific overrides (affiliate_id + optional campaign filter),
 * example-calc preview (conversions -> tier + bonus).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useBonusStructureCreate,
	useBonusStructureForPartner,
	useBonusStructureList,
} from "./hooks";
import {
	calcBonusCents,
	formatCents,
	isActive,
	pickTier,
	type BonusTierRow,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const LABEL = "text-xs font-medium text-zinc-700";
const TH = "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500";
const TD = "px-3 py-2 text-sm text-zinc-900";

function fmtWindow(starts_at: number, ends_at: number): string {
	const s = new Date(starts_at * 1000).toISOString().slice(0, 10);
	const e = new Date(ends_at * 1000).toISOString().slice(0, 10);
	return `${s} -> ${e}`;
}

export function BonusStructureDisplayForPartnersPage() {
	const listQuery = useBonusStructureList();
	const createMut = useBonusStructureCreate();

	const [bonus_id, setBonusId] = useState("");
	const [campaign_id, setCampaignId] = useState("");
	const [tier_name, setTierName] = useState("");
	const [threshold_conversions, setThreshold] = useState("");
	const [bonus_amount_cents, setAmount] = useState("");
	const [starts_at, setStartsAt] = useState("");
	const [ends_at, setEndsAt] = useState("");

	const [partnerId, setPartnerId] = useState("");
	const [partnerCampaign, setPartnerCampaign] = useState("");
	const [previewConv, setPreviewConv] = useState("0");

	const partnerQuery = useBonusStructureForPartner({
		affiliate_id: partnerId.trim(),
		campaign_id: partnerCampaign.trim() || undefined,
	});

	const items: BonusTierRow[] = listQuery.data?.items ?? [];

	const previewConvNum = useMemo(() => {
		const n = Number(previewConv);
		return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
	}, [previewConv]);

	const partnerItems: BonusTierRow[] = partnerQuery.data?.items ?? [];
	const previewTier = useMemo(
		() => pickTier(partnerItems, previewConvNum),
		[partnerItems, previewConvNum],
	);
	const previewCents = useMemo(
		() => calcBonusCents(partnerItems, previewConvNum),
		[partnerItems, previewConvNum],
	);

	const canCreate =
		bonus_id.trim() &&
		campaign_id.trim() &&
		tier_name.trim() &&
		threshold_conversions.trim() &&
		bonus_amount_cents.trim() &&
		starts_at.trim() &&
		ends_at.trim() &&
		!createMut.isPending;

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		if (!canCreate) return;
		createMut.mutate(
			{
				bonus_id: bonus_id.trim(),
				campaign_id: campaign_id.trim(),
				tier_name: tier_name.trim(),
				threshold_conversions: Math.max(0, Math.floor(Number(threshold_conversions))),
				bonus_amount_cents: Math.max(0, Math.floor(Number(bonus_amount_cents))),
				starts_at: Math.floor(Number(starts_at)),
				ends_at: Math.floor(Number(ends_at)),
			},
			{
				onSuccess: () => {
					setBonusId("");
					setTierName("");
					setThreshold("");
					setAmount("");
				},
			},
		);
	};

	const nowSec = Math.floor(Date.now() / 1000);

	return (
		<main
			data-testid="bonus-structure-display-for-partners-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Partner bonuses
				</p>
				<h1 className="text-3xl font-semibold text-green-700">
					Bonus structure
				</h1>
				<p className="text-sm text-zinc-600">
					Tiers define min-volume thresholds and bonus payouts per campaign.
					Partners see the subset currently active on their campaign.
				</p>
			</header>

			<section className={CARD} aria-label="Create bonus tier">
				<h2 className="text-base font-semibold text-zinc-900">Add tier</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="bonus-structure-display-for-partners-create-form"
				>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>bonus_id</span>
						<input
							type="text"
							className={INPUT}
							value={bonus_id}
							onChange={(e) => setBonusId(e.currentTarget.value)}
							data-testid="bonus-structure-display-for-partners-bonus-id"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>campaign_id</span>
						<input
							type="text"
							className={INPUT}
							value={campaign_id}
							onChange={(e) => setCampaignId(e.currentTarget.value)}
							data-testid="bonus-structure-display-for-partners-campaign-id"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>tier_name</span>
						<input
							type="text"
							className={INPUT}
							value={tier_name}
							onChange={(e) => setTierName(e.currentTarget.value)}
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>threshold_conversions</span>
						<input
							type="number"
							min={0}
							className={INPUT}
							value={threshold_conversions}
							onChange={(e) => setThreshold(e.currentTarget.value)}
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>bonus_amount_cents</span>
						<input
							type="number"
							min={0}
							className={INPUT}
							value={bonus_amount_cents}
							onChange={(e) => setAmount(e.currentTarget.value)}
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>starts_at (unix-sec)</span>
						<input
							type="number"
							className={INPUT}
							value={starts_at}
							onChange={(e) => setStartsAt(e.currentTarget.value)}
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>ends_at (unix-sec)</span>
						<input
							type="number"
							className={INPUT}
							value={ends_at}
							onChange={(e) => setEndsAt(e.currentTarget.value)}
						/>
					</label>
					<div className="flex items-end">
						<button
							type="submit"
							className={BTN}
							disabled={!canCreate}
							data-testid="bonus-structure-display-for-partners-submit"
						>
							{createMut.isPending ? "Saving" : "Add tier"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="All tiers">
				<h2 className="text-base font-semibold text-zinc-900">All tiers</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bonus-structure-display-for-partners-loading"
					>
						Loading
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bonus-structure-display-for-partners-empty"
					>
						No tiers configured yet.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table
							className="min-w-full divide-y divide-zinc-200"
							data-testid="bonus-structure-display-for-partners-list"
						>
							<thead>
								<tr>
									<th className={TH}>Tier</th>
									<th className={TH}>Campaign</th>
									<th className={TH}>Min volume</th>
									<th className={TH}>Bonus</th>
									<th className={TH}>Active window</th>
									<th className={TH}>Status</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((row) => {
									const active = isActive(row, nowSec);
									return (
										<tr
											key={row.id}
											data-testid={`bonus-structure-display-for-partners-row-${row.id}`}
										>
											<td className={TD}>{row.tier_name}</td>
											<td className={`${TD} font-mono text-xs`}>
												{row.campaign_id}
											</td>
											<td className={TD}>{row.threshold_conversions}</td>
											<td className={TD}>{formatCents(row.bonus_amount_cents)}</td>
											<td className={`${TD} font-mono text-xs text-zinc-600`}>
												{fmtWindow(row.starts_at, row.ends_at)}
											</td>
											<td className={TD}>
												<span
													className={
														active
															? "rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
															: "rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600"
													}
												>
													{active ? "Active" : "Inactive"}
												</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<section className={CARD} aria-label="Partner-specific overrides">
				<h2 className="text-base font-semibold text-zinc-900">
					Partner-specific overrides
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Enter a partner + optional campaign filter to see the active tiers they qualify for.
				</p>
				<div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
					<label className="flex flex-col gap-1">
						<span className={LABEL}>affiliate_id</span>
						<input
							type="text"
							className={INPUT}
							value={partnerId}
							onChange={(e) => setPartnerId(e.currentTarget.value)}
							data-testid="bonus-structure-display-for-partners-partner-input"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className={LABEL}>campaign_id (optional)</span>
						<input
							type="text"
							className={INPUT}
							value={partnerCampaign}
							onChange={(e) => setPartnerCampaign(e.currentTarget.value)}
						/>
					</label>
				</div>
				<div className="mt-4">
					{!partnerId.trim() ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="bonus-structure-display-for-partners-partner-hint"
						>
							Enter an affiliate_id to load overrides.
						</p>
					) : partnerQuery.isLoading ? (
						<p className="text-sm text-zinc-500">Loading partner overrides</p>
					) : partnerQuery.error ? (
						<p className="text-sm text-red-600" role="alert">
							{partnerQuery.error.message}
						</p>
					) : partnerItems.length === 0 ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="bonus-structure-display-for-partners-partner-empty"
						>
							No active tiers for this partner right now.
						</p>
					) : (
						<ul
							className="divide-y divide-zinc-200"
							data-testid="bonus-structure-display-for-partners-partner-list"
						>
							{partnerItems.map((row) => (
								<li
									key={row.id}
									className="flex items-center justify-between py-2 text-sm"
								>
									<span>
										<strong className="text-zinc-900">{row.tier_name}</strong>{" "}
										<span className="text-zinc-500">
											({row.threshold_conversions}+ conv)
										</span>
									</span>
									<span className="font-medium text-green-700">
										{formatCents(row.bonus_amount_cents)}
									</span>
								</li>
							))}
						</ul>
					)}
				</div>
			</section>

			<section className={CARD} aria-label="Example calculation">
				<h2 className="text-base font-semibold text-zinc-900">
					Example calculation
				</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Simulate bonus earned at a conversion count. Uses the currently-loaded partner tiers.
				</p>
				<label className="mt-3 flex max-w-xs flex-col gap-1">
					<span className={LABEL}>conversions</span>
					<input
						type="number"
						min={0}
						className={INPUT}
						value={previewConv}
						onChange={(e) => setPreviewConv(e.currentTarget.value)}
						data-testid="bonus-structure-display-for-partners-preview-input"
					/>
				</label>
				<div
					className="mt-4 rounded-[6px] border border-green-200 bg-green-50 p-4"
					data-testid="bonus-structure-display-for-partners-preview"
				>
					<p className="text-xs font-semibold uppercase tracking-wider text-green-700">
						Preview
					</p>
					<p className="mt-1 text-sm text-zinc-900">
						At <strong>{previewConvNum}</strong> conversions, tier reached:{" "}
						<strong data-testid="bonus-structure-display-for-partners-preview-tier">
							{previewTier ? previewTier.tier_name : "None"}
						</strong>
					</p>
					<p className="mt-1 text-2xl font-semibold text-green-700">
						<span data-testid="bonus-structure-display-for-partners-preview-amount">
							{formatCents(previewCents)}
						</span>
					</p>
				</div>
			</section>
		</main>
	);
}

export default BonusStructureDisplayForPartnersPage;
